import { Box, Flex, List, ListItem } from "@chakra-ui/react";
import React from "react";
import { usePopper } from "react-popper";
import { useDebounce } from "use-debounce";
import {
  AddressModel,
  LocationModel as Location,
} from "../../../../api/models";
import { addressMaxLength } from "../../../../defines";
import { useOnClickOutside } from "../../../../hooks";
import { HiddenInputToReceiveFocus } from "../../../../react-hook-form";
import {
  geocodePlace,
  loadGooglePlaces,
  searchAddress,
} from "../../../../utils";
import { buildLocation, buildStreetAddress } from "../../../../utils/addresses";
import { VeriInput } from "../../VeriInput";
import {
  AutocompleteAddressProps,
  AutocompleteAddressSelection,
} from "./AutocompleteAddress.types";

export const AutocompleteAddress = React.forwardRef<
  HTMLInputElement,
  AutocompleteAddressProps
>(
  (
    {
      label,
      placeholder,
      inputName,
      bgHoverColor,
      allowCreation,
      notFoundText,
      onChange,
      onSelect,
      baseName = "",
      value,
      error,
      isFullAddress = false,
      inputProps,
      idDefaultValue,
      form,
    },
    ref,
  ) => {
    const [options, setOptions] = React.useState<
      google.maps.places.AutocompletePrediction[] | null | undefined
    >(undefined);
    const [displayOptions, setDisplayOptions] = React.useState(false);
    const [inputValue, setInputValue] = React.useState(value);
    const [loaded, setLoaded] = React.useState(false);
    const [debouncedValue] = useDebounce(inputValue, 500);
    const [lastDebounced, setLastDebounced] = React.useState<
      string | null | undefined
    >();

    const [rootEl, setRootEl] = React.useState<HTMLDivElement | null>(null);
    const [trigger, setTrigger] = React.useState<HTMLInputElement | null>(null);
    const [popperElement, setPopperElement] =
      React.useState<HTMLUListElement | null>(null);
    const { styles, attributes } = usePopper(trigger, popperElement, {
      modifiers: [],
    });

    useOnClickOutside(rootEl, setDisplayOptions.bind(null, false));

    React.useEffect(() => {
      if (loaded && debouncedValue && debouncedValue !== lastDebounced)
        searchAddress(debouncedValue).then((addresses) => {
          setOptions(addresses);

          setLastDebounced(debouncedValue);
        });
    }, [debouncedValue]);

    React.useEffect(() => {
      setLastDebounced(value);
      setInputValue(value);
    }, [value]);

    React.useEffect(() => {
      loadGooglePlaces(() => {
        setLoaded(true);
      });
    }, []);

    const filterOptions = (inputValue: string | null | undefined) => {
      if (inputValue) {
        setInputValue(inputValue);
        if (onChange) onChange(inputValue);
      } else {
        setInputValue("");
        if (onChange) onChange("");
      }
    };

    const selectOption = (
      option: google.maps.places.AutocompletePrediction,
    ) => {
      if (option.place_id) {
        geocodePlace(option.place_id).then((address) => {
          const location: Location = buildLocation(address);
          if (location) {
            const streetAddress = buildStreetAddress(location);
            setLastDebounced(
              isFullAddress ? location.formattedAddress : streetAddress,
            );
            setInputValue(
              isFullAddress ? location.formattedAddress : streetAddress,
            );

            // brooklyn issue no locality then sublocality
            if (location.sublocality && !location.locality) {
              location.locality = location.sublocality;
              location.sublocality = null;
            }

            // change homeAddress2 sublocality

            const base = baseName && baseName.length > 0 ? baseName + "." : "";

            const setFormValue = (
              name: keyof AddressModel,
              value: string | null | undefined,
            ) => {
              form.setValue(base + name, value, {
                // All our forms except a couple of exceptions use the default useForm({ mode: "onSubmitted" })
                // If the mode: "onChanged" | "onTouched" | "onBlur", this fix will still not work in all cases

                // The alternative would be to always validate, which would bring another issue:
                // 1. User selects autocomplete without all the required fields => validates and displays error messages
                // 2. User manually changes the inputs with the error messages => error message does not disappear
                shouldValidate: form.formState.isSubmitted,
                shouldDirty: true,
              });
            };

            setFormValue("address1", streetAddress);
            setFormValue("address2", location.sublocality);
            setFormValue("city", location.locality);
            setFormValue("region", location.administrativeAreaLevel1);
            setFormValue("postalCode", location.postalCode);
            setFormValue("postalCodeSuffix", location.postalCodeSuffix);
            setFormValue("country", location.country);
            setFormValue("id", idDefaultValue);

            form.setValue(base + "location", location);

            if (onSelect) {
              const data: AutocompleteAddressSelection = {
                address1: streetAddress || "",
                address2: location.sublocality || "",
                city: location.locality || "",
                region: location.administrativeAreaLevel1 || "",
                postalCode: location.postalCode || "",
                postalCodeSuffix: location.postalCodeSuffix || "",
                country: location.country || "",
              };
              onSelect(data);
            }
          }
        });
      }
    };

    const selectOptionFromList = (
      option: google.maps.places.AutocompletePrediction,
    ) => {
      selectOption(option);
      setDisplayOptions(false);
    };

    const watchedValue = inputName ? form.watch(inputName) : inputValue;

    return (
      <Box position="relative" ref={setRootEl}>
        <Flex>
          <HiddenInputToReceiveFocus ref={ref} />
          <VeriInput
            {...inputProps}
            label={label}
            name={inputProps?.name || inputName}
            placeholder={placeholder}
            onChange={(e) => {
              filterOptions(e.currentTarget.value);
              if (inputProps?.onChange) {
                inputProps.onChange(e);
              }
            }}
            error={error}
            autoComplete="false"
            ref={setTrigger}
            value={watchedValue || ""}
            onKeyDown={(e) => {
              if (e.key === "Tab") {
                setDisplayOptions(false);
              }
            }}
            onFocus={() => setDisplayOptions(true)}
            maxLength={addressMaxLength}
          />
        </Flex>

        {displayOptions && options && options?.length > 0 && (
          <List
            bg="white"
            zIndex={100}
            width="100%"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="md"
            boxShadow="6px 5px 8px rgba(0,50,30,0.02)"
            my={2}
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
          >
            {options?.map((option) => {
              return (
                <ListItem
                  key={option.place_id}
                  _hover={{ bg: bgHoverColor || "gray.100" }}
                  my={1}
                  p={2}
                  cursor="pointer"
                  onClick={() => selectOptionFromList(option)}
                >
                  <Flex align="center">{option.description}</Flex>
                </ListItem>
              );
            })}
            {!options?.length && !allowCreation && (
              <ListItem my={1} p={2} data-testid="not-found">
                <Flex align="center">{notFoundText}</Flex>
              </ListItem>
            )}
          </List>
        )}
      </Box>
    );
  },
);

AutocompleteAddress.displayName = "AutocompleteAddress";
